import { useCallback, useRef, useState } from 'react'

import { LOCALE_COOKIE } from '@src/config/localisation/locale-config.constants'
import { REGION_ID_COOKIE } from '@src/config/localisation/locale-config.static-constants'
import type { TAbsoluteLocale, TCountry, TRegionDetail } from '@src/config/localisation/locale-config.types'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { getCookieStorage } from '@src/utils/enhancedCookie'
import { noop } from '@src/utils/functions/utils'
import { getStandardLocale, parseLocale, updateCommunicationPreference } from '@src/utils/i18n.utils'

interface TUseLocaleContextHandlersInput {
  regionDetails: TRegionDetail
  setLocale: (locale: TAbsoluteLocale) => void
  setIsLocaleConfirmationModalOpen: (isLocaleConfirmationModalOpen: boolean) => void
  setConfirmationLocale: (confirmationLocale: TAbsoluteLocale | TCountry | null) => void
}

interface TUseLocaleContextHandlers {
  handleSetLocale: (
    locale: TAbsoluteLocale,
    clientSide?: boolean,
    shouldUpdateCommunicationPreference?: boolean,
  ) => Promise<void> | void
  closeConfirmationModal: (confirm: boolean) => void
  showConfirmationModal: (confirmationLocale: TAbsoluteLocale, callback: () => void) => void
  onConfirm: () => void
}

const useLocaleContextHandlers = ({
  regionDetails,
  setLocale,
  setIsLocaleConfirmationModalOpen,
  setConfirmationLocale,
}: TUseLocaleContextHandlersInput): TUseLocaleContextHandlers => {
  const [onConfirm, setOnConfirm] = useState<() => void>(noop)
  const communicationPreferenceAbortController = useRef<AbortController | null>(null)
  const handleSetLocale = useCallback(
    async (locale: TAbsoluteLocale, clientSide = false, shouldUpdateCommunicationPreference = true) => {
      const loggedIn = AuthUtil.isLoggedIn()
      const updateLocaleSettings = () => {
        // Do NOT seed the new region into localStorage here. Internal-analytics events read
        // region_id from this localStorage entry (InternalAnalytics.ts), and IA endpoints are
        // per-region and deployment-baked, so flipping region_id before the switch navigates/reloads
        // routes the transitional switch event (e.g. locale_switch_modal_saved) to the origin
        // region's instance — a US event landing in the UK instance right after a UK->US switch
        // (STRFRNT-15479). It's also redundant: the locale.context mount effect re-seeds localStorage
        // from the SSR-resolved locale on the very next load (hard nav or reload).
        getCookieStorage().setCookieLocalized(LOCALE_COOKIE, locale, { isGlobal: true })
        // Keep REGION_ID_COOKIE in lockstep with LOCALE_COOKIE so client-side reads
        // (e.g. getLocalisationHeaders) don't combine fresh locale with stale regionId
        // in the window before the next SSR rewrites both via resolveLocaleData.
        const { country } = parseLocale(locale)
        const regionId = regionDetails.regions[country]?.regionId
        if (regionId != null) {
          getCookieStorage().setCookieLocalized(REGION_ID_COOKIE, String(regionId), { isGlobal: true })
        }
        setLocale(locale)
      }
      if (shouldUpdateCommunicationPreference && loggedIn) {
        communicationPreferenceAbortController.current?.abort()
        communicationPreferenceAbortController.current = new AbortController()

        try {
          // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
          const response = await updateCommunicationPreference({
            locale: getStandardLocale(locale),
            regionDetails,
            abortController: communicationPreferenceAbortController.current,
          })
          if (response?.status === 200) {
            updateLocaleSettings()
          }
        } catch (error) {
          if (communicationPreferenceAbortController.current.signal.aborted) return
          console.error('Failed to update preferred language and communication preference: ', error)
        }
      } else {
        updateLocaleSettings()
      }
      if (!clientSide) window.location.reload()
    },
    [regionDetails, setLocale],
  )

  const closeConfirmationModal = useCallback(
    (confirm: boolean) => {
      if (confirm) onConfirm()
      setIsLocaleConfirmationModalOpen(false)
      setOnConfirm(noop)
      setConfirmationLocale(null)
    },
    [setIsLocaleConfirmationModalOpen, setOnConfirm, setConfirmationLocale, onConfirm],
  )

  const showConfirmationModal = useCallback(
    (confirmationLocale: TAbsoluteLocale, callback: () => void) => {
      setConfirmationLocale(confirmationLocale)
      setOnConfirm(() => callback)
      setIsLocaleConfirmationModalOpen(true)
    },
    [setIsLocaleConfirmationModalOpen, setConfirmationLocale],
  )

  return {
    handleSetLocale,
    closeConfirmationModal,
    showConfirmationModal,
    onConfirm,
  }
}

export default useLocaleContextHandlers
